import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { useReadingActivity } from '@/features/library/hooks/useReadingActivity.js';

/** Minutes par page : estimation conservatrice pour le temps de lecture. */
const MINUTES_PER_PAGE = 1.5;

function computeStreak(cells: ReadonlyArray<{ level: number }>): number {
  let max = 0;
  let current = 0;
  for (const c of cells) {
    if (c.level > 0) {
      current += 1;
      if (current > max) max = current;
    } else {
      current = 0;
    }
  }
  return max;
}

function formatHours(totalPages: number): string {
  if (totalPages === 0) return '—';
  const hours = (totalPages * MINUTES_PER_PAGE) / 60;
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 100) return `${Math.round(hours)} h`;
  return `${Math.round(hours).toLocaleString('fr-FR')} h`;
}

interface StatCardProps {
  icon: string;
  iconBg: 'sage' | 'rose' | 'gold' | 'cream' | 'terracotta' | 'brown';
  value: string;
  label: string;
}

const ICON_BG_CLASSES: Record<StatCardProps['iconBg'], string> = {
  sage: 'bg-sage-light',
  rose: 'bg-rose-light',
  gold: 'bg-gold-light',
  cream: 'bg-cream-dark',
  terracotta: 'bg-terracotta-light',
  brown: 'bg-brown text-cream-light',
};

function StatCard({ icon, iconBg, value, label }: StatCardProps): JSX.Element {
  return (
    <div className="rounded-2xl bg-paper p-4 shadow-md">
      <div
        className={`mb-2 flex h-8 w-8 items-center justify-center rounded-xl text-base ${ICON_BG_CLASSES[iconBg]}`}
      >
        {icon}
      </div>
      <p className="font-serif text-lg font-semibold leading-none text-brown">
        {value}
      </p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-brown-soft">
        {label}
      </p>
    </div>
  );
}

/**
 * Stats grid 6 cards.
 * MVP : 4 stats calculées depuis useMyShelf, 2 placeholder.
 * Plus tard : endpoint /profile/stats avec pages lues réelles, temps de
 * lecture, streak, etc.
 */
export function StatsGrid(): JSX.Element {
  const { data } = useMyShelf();
  const { data: activity } = useReadingActivity();
  const entries = data?.items ?? [];
  const total = entries.length;
  const done = entries.filter((e) => e.status === 'done').length;
  const rated = entries.filter((e) => e.rating !== null);
  const avgRating =
    rated.length > 0
      ? rated.reduce((s, e) => s + (e.rating ?? 0), 0) / rated.length
      : 0;

  const totalPages = entries.reduce(
    (sum, e) => sum + (e.status === 'done' ? (e.book.pages ?? 0) : 0),
    0,
  );
  const streak = activity?.cells ? computeStreak(activity.cells) : 0;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <StatCard
        icon="📖"
        iconBg="sage"
        value={totalPages.toLocaleString('fr-FR')}
        label="pages lues"
      />
      <StatCard
        icon="⏱️"
        iconBg="rose"
        value={formatHours(totalPages)}
        label="temps lecture"
      />
      <StatCard
        icon="⭐"
        iconBg="gold"
        value={avgRating > 0 ? avgRating.toFixed(1) : '—'}
        label="note moy."
      />
      <StatCard
        icon="🔥"
        iconBg="cream"
        value={streak > 0 ? `${streak} j` : '—'}
        label="streak"
      />
      <StatCard
        icon="📚"
        iconBg="terracotta"
        value={String(total)}
        label="livres total"
      />
      <StatCard icon="✨" iconBg="brown" value={String(done)} label="terminés" />
    </div>
  );
}
